import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { TestAccountsPanel } from "@/components/TestAccountsPanel";
import { StripePanel } from "@/components/StripePanel";
import { ChatReportsPanel } from "@/components/ChatReportsPanel";
import { api, errorMessage } from "@/lib/api";

function Metric({ label, value, suffix = "" }) {
  return <div className="ga-card p-4"><div className="ga-muted text-xs">{label}</div><div className="mt-1 text-2xl font-semibold">{value}{suffix}</div></div>;
}

export default function Admin() {
  const [m, setM] = useState(null);
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    Promise.all([api.get("/admin/metrics"), api.get("/admin/settings")]).then(([a, b]) => { setM(a.data); setS(b.data); }).catch((e) => toast.error(errorMessage(e)));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings", { free_limits: s.free_limits, premium_storage_mb: Number(s.premium_storage_mb), price_monthly: Number(s.price_monthly), price_yearly: Number(s.price_yearly), features: s.features });
      setS(data); toast.success("Configurações salvas.");
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const setLimit = (k) => (e) => setS({ ...s, free_limits: { ...s.free_limits, [k]: Number(e.target.value) } });
  const setFeat = (plan) => (e) => setS({ ...s, features: { ...s.features, [plan]: e.target.value.split("\n").filter(Boolean) } });

  return (
    <div className="pb-10">
      <PageHeader title="Painel administrativo" subtitle="Somente dados agregados. Nenhum conteúdo privado é exibido." />
      {m && (
        <div className="grid grid-cols-2 gap-2.5 px-5" data-testid="admin-metrics">
          <Metric label="Usuários" value={m.users} />
          <Metric label="Usuários ativos (30d)" value={m.active_users} />
          <Metric label="Gratuitos" value={m.free_users} />
          <Metric label="Premium" value={m.premium_users} />
          <Metric label="Memórias" value={m.memories} />
          <Metric label="Retenção (7d)" value={m.retention_rate} suffix="%" />
          <Metric label="Voz no mês" value={m.voice_month} />
          <Metric label="Fotos no mês" value={m.photos_month} />
          <Metric label="Assinaturas ativas" value={m.subscriptions?.active} />
          <Metric label="Cancelamentos agendados" value={m.subscriptions?.cancel_scheduled} />
          <Metric label="Pagamentos falhos" value={m.subscriptions?.past_due} />
          <Metric label="Docs/vídeos no mês" value={m.subscriptions?.documents_month} />
          <Metric label="Conversão premium" value={m.conversion_rate} suffix="%" />
          <Metric label="Perguntas feitas" value={m.questions} />
        </div>
      )}
      {s && (
        <div className="ga-card mx-5 mt-5 space-y-3 p-4" data-testid="admin-settings">
          <h2 className="text-sm font-semibold">Plano gratuito — limites</h2>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <label>Memórias<input type="number" value={s.free_limits.memories} onChange={setLimit("memories")} className="ga-input mt-1 py-2" data-testid="admin-limit-memories" /></label>
            <label>Voz/mês<input type="number" value={s.free_limits.voice_month} onChange={setLimit("voice_month")} className="ga-input mt-1 py-2" data-testid="admin-limit-voice" /></label>
            <label>Fotos/mês<input type="number" value={s.free_limits.photos_month} onChange={setLimit("photos_month")} className="ga-input mt-1 py-2" data-testid="admin-limit-photos" /></label>
            <label>Histórico (dias)<input type="number" value={s.free_limits.history_days} onChange={setLimit("history_days")} className="ga-input mt-1 py-2" data-testid="admin-limit-history" /></label>
            <label>Armazenamento Free (MB)<input type="number" value={s.free_limits.storage_mb ?? 500} onChange={setLimit("storage_mb")} className="ga-input mt-1 py-2" data-testid="admin-limit-storage" /></label>
            <label>Armazenamento Premium (MB)<input type="number" value={s.premium_storage_mb ?? 5120} onChange={(e) => setS({ ...s, premium_storage_mb: Number(e.target.value) })} className="ga-input mt-1 py-2" data-testid="admin-premium-storage" /></label>
          </div>
          <h2 className="pt-2 text-sm font-semibold">Preços Premium</h2>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <label>Mensal (R$)<input type="number" step="0.01" value={s.price_monthly} onChange={(e) => setS({ ...s, price_monthly: e.target.value })} className="ga-input mt-1 py-2" data-testid="admin-price-monthly" /></label>
            <label>Anual (R$)<input type="number" step="0.01" value={s.price_yearly} onChange={(e) => setS({ ...s, price_yearly: e.target.value })} className="ga-input mt-1 py-2" data-testid="admin-price-yearly" /></label>
          </div>
          <h2 className="pt-2 text-sm font-semibold">Funcionalidades dos planos (uma por linha)</h2>
          {["free", "plus", "familia"].map((p) => <label key={p} className="block text-xs capitalize">{p}<textarea rows={4} value={s.features[p].join("\n")} onChange={setFeat(p)} className="ga-input mt-1 py-2 text-xs" data-testid={`admin-features-${p}`} /></label>)}
          <button onClick={save} disabled={busy} className="ga-btn-primary w-full" data-testid="admin-save-button">{busy ? "Salvando..." : "Salvar configurações"}</button>
        </div>
      )}
      <StripePanel />
      <ChatReportsPanel />
      <TestAccountsPanel />
    </div>
  );
}
